import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Box
} from '@mui/material';
import { Help, Close } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

interface SupportButtonProps {
  chatId?: string;
  reportId?: string;
  context?: 'chat' | 'report' | 'general';
  variant?: 'text' | 'outlined' | 'contained';
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
  showIcon?: boolean;
  label?: string;
  sx?: any;
}

const SupportButton: React.FC<SupportButtonProps> = ({
  chatId,
  reportId,
  context = 'general',
  variant = 'outlined',
  size = 'medium',
  fullWidth = false,
  showIcon = true,
  label = 'Get Support',
  sx = {}
}) => {
  const { userProfile } = useAuth();
  const [open, setOpen] = useState(false);
  const [problem, setProblem] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleOpen = () => {
    setOpen(true);
    setSuccess(false);
    setError(null);
    setProblem('');
  };

  const handleClose = () => {
    setOpen(false);
    setProblem('');
    setError(null);
    setSuccess(false);
  };

  const handleSubmit = async () => {
    if (!problem.trim()) {
      setError('Please describe the problem');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await api.post('/support/ticket', {
        problem_description: problem.trim(),
        chat_id: chatId || null,
        report_id: reportId || null,
        context,
        user_email: userProfile?.email,
        user_name: userProfile?.nickname || userProfile?.email
      });

      setSuccess(true);
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err: any) {
      console.error('Failed to submit support ticket:', err);
      
      // Handle different types of error responses
      let errorMessage = 'Failed to submit support request. Please try again.';
      
      if (err.response?.data) {
        if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        } else if (err.response.data.detail) {
          if (typeof err.response.data.detail === 'string') {
            errorMessage = err.response.data.detail;
          } else if (Array.isArray(err.response.data.detail)) {
            // Handle validation errors
            errorMessage = err.response.data.detail.map((e: any) => 
              typeof e === 'string' ? e : e.msg || 'Validation error'
            ).join(', ');
          } else {
            errorMessage = 'Validation error occurred';
          }
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getContextDescription = () => {
    switch (context) {
      case 'chat':
        return chatId ? `Chat ID: ${chatId}` : 'Related to current chat';
      case 'report':
        return reportId ? `Report ID: ${reportId}` : 'Related to current report';
      default:
        return 'General support request';
    }
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        fullWidth={fullWidth}
        onClick={handleOpen}
        startIcon={showIcon ? <Help /> : undefined}
        sx={{ 
          textTransform: 'none',
          ...(variant === 'text' && { 
            color: 'text.secondary',
            '&:hover': { backgroundColor: 'action.hover' }
          }),
          ...sx
        }}
      >
        {label}
      </Button>

      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Get Support
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {success ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              Support request submitted successfully! We'll get back to you soon.
            </Alert>
          ) : (
            <>
              <Typography variant="body1" sx={{ mb: 3, fontWeight: 500 }}>
                For the fastest support, please join our Discord community:
              </Typography>

              <Button
                variant="contained"
                color="primary"
                href="https://discord.gg/ANDmEzJM"
                target="_blank"
                rel="noopener noreferrer"
                fullWidth
                sx={{ mb: 3, py: 1.5 }}
              >
                Join Discord Support Channel
              </Button>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                — OR —
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Submit a support request (we'll respond via email):
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {getContextDescription()}
              </Typography>

              <TextField
                fullWidth
                multiline
                rows={4}
                value={problem}
                onChange={(e) => setProblem(e.target.value)}
                placeholder="Describe the issue you're having..."
                disabled={loading}
                error={!!error}
                helperText={error || ''}
                sx={{ mb: 2 }}
              />

              <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                <Typography variant="caption" color="text.secondary">
                  <strong>Context Information:</strong><br />
                  User: {userProfile?.email || 'Not logged in'}<br />
                  Page: {context} {chatId && `(Chat: ${chatId})`} {reportId && `(Report: ${reportId})`}
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>

        {!success && (
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained" 
              disabled={loading || !problem.trim()}
              startIcon={loading ? <CircularProgress size={20} /> : undefined}
            >
              {loading ? 'Submitting...' : 'Submit Support Request'}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </>
  );
};

export default SupportButton;